/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { useCallback } from 'react'
import BaseModal from 'src/components/ts-components/BaseModal'

type AdditionalInfoModalProps = {
  isOpen: boolean
  onClose: () => void
  content: string
  title?: string
}

const AdditionalInfoModal = ({
  isOpen,
  onClose,
  content,
  title = 'Additional Info',
}: AdditionalInfoModalProps) => {
  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      cancelText='Close'
      desktopWidth='30vw'
    >
      <div
        css={css`
          padding: 8px;
          max-height: 70vh;
          overflow-y: auto;
          color: ${colors.WHITE};
          font-family: inherit;
          line-height: 1.6;

          /* Remove default margins and excessive spacing */
          * {
            margin: 0;
            padding: 0;
          }

          /* Ensure first element has no top margin */
          *:first-child {
            margin-top: 0 !important;
          }

          /* Ensure last element has no bottom margin */
          *:last-child {
            margin-bottom: 0 !important;
          }

          /* Style the rich text content with minimal spacing */
          h1,
          h2,
          h3 {
            margin: 16px 0 8px 0;
            font-weight: 600;
            color: ${colors.WHITE};
          }

          h1 {
            font-size: 1.75em;
          }
          h2 {
            font-size: 1.5em;
          }
          h3 {
            font-size: 1.25em;
          }

          p {
            margin: 8px 0;
            color: ${colors.WHITE};
          }

          /* Force list styles - using pseudo-elements as workaround for global reset */
          ul {
            margin: 8px 0 !important;
            padding-left: 0 !important;
            color: ${colors.WHITE} !important;
            list-style: none !important;
          }

          ol {
            margin: 8px 0 !important;
            padding-left: 0 !important;
            color: ${colors.WHITE} !important;
            list-style: none !important;
            counter-reset: list-counter !important;
          }

          ul li {
            margin: 4px 0 !important;
            padding-left: 28px !important;
            color: ${colors.WHITE} !important;
            display: block !important;
            position: relative !important;
          }

          ol li {
            margin: 4px 0 !important;
            padding-left: 28px !important;
            color: ${colors.WHITE} !important;
            display: block !important;
            position: relative !important;
            counter-increment: list-counter !important;
          }

          /* Create custom bullet points and numbers */
          ul li::before {
            content: '•' !important;
            color: ${colors.WHITE} !important;
            font-size: 16px !important;
            line-height: inherit !important;
            position: absolute !important;
            left: 8px !important;
            top: 0 !important;
          }

          ol li::before {
            content: counter(list-counter) '.' !important;
            color: ${colors.WHITE} !important;
            font-weight: bold !important;
            line-height: inherit !important;
            position: absolute !important;
            left: 8px !important;
            top: 0 !important;
            min-width: 20px !important;
          }

          /* Support for nested lists */
          ul ul {
            margin: 4px 0 !important;
            padding-left: 24px !important;
          }

          ol ol {
            margin: 4px 0 !important;
            padding-left: 24px !important;
          }

          ul ol,
          ol ul {
            margin: 4px 0 !important;
            padding-left: 24px !important;
          }

          strong {
            font-weight: 600;
            color: ${colors.WHITE};
          }

          em {
            font-style: italic;
            color: ${colors.WHITE};
          }

          u {
            text-decoration: underline;
            color: ${colors.WHITE};
          }

          s {
            text-decoration: line-through;
            color: ${colors.WHITE};
          }

          a {
            color: ${colors.PRIMARY};
            text-decoration: underline;
          }

          img {
            max-width: 100%;
            height: auto;
            border-radius: 4px;
          }

          blockquote {
            border-left: 4px solid ${colors.PRIMARY};
            padding-left: 16px;
            margin: 12px 0;
            font-style: italic;
            opacity: 0.9;
            color: ${colors.WHITE};
          }

          /* Remove empty paragraphs and BR tags that create whitespace */
          p:empty {
            display: none;
          }

          br {
            display: none;
          }

          /* Text alignment classes from Quill */
          .ql-align-left {
            text-align: left;
          }
          .ql-align-center {
            text-align: center;
          }
          .ql-align-right {
            text-align: right;
          }
          .ql-align-justify {
            text-align: justify;
          }
        `}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    </BaseModal>
  )
}

export default AdditionalInfoModal
